import { useAccessibility } from "../../../context/AccessibilityContext/AccessibilityContext";
import Block from "../../../components/Block/Block";
import FadeIn from "../../../components/FadeIn/FadeIn";
import Link from "../../../components/Link/Link";
import style from './requirementsblock.module.scss';
import { useEffect, useState } from 'react';
import { fetchRequirementsInfo } from '../../../services/api'; // Новая функция

const RequirementsBlock = () => {
    const { settings } = useAccessibility();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Accessibility-aware colors
    const backgroundColor = settings.contrast === 'default' ? 'rgba(255, 255, 255, 0.85)' : 'var(--bg-primary)';
    const textColor = settings.contrast === 'default' ? '#231f20' : 'var(--text-primary)';
    const linkColor = settings.contrast === 'default' ? '#1d3e8c' : 'var(--accent-color)';

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetchRequirementsInfo();
                // Данные теперь в response.data[0].attributes
                setData(response.data[0].attributes);
                console.log('Requirements data:', response.data[0].attributes);
            } catch (err) {
                console.error('Error fetching requirements data:', err);
                setError('Не удалось загрузить данные. Пожалуйста, попробуйте позже.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // File link component
    const DocumentLink = ({ document, fallbackUrl, fallbackText, fallbackAriaLabel }) => {
        // Если есть файл
        if (document?.File?.data?.attributes?.url) {
            return (
                <Link
                    href={`${document.File.data.attributes.url}`}
                    children={`${document.Name} (${document.File.data.attributes.ext || 'PDF'})`}
                    color={linkColor}
                    fontFamily='Roboto, sans-serif'
                    fontSize='1rem'
                    fontWeight='500'
                    aria-label={`Скачать документ: ${document.Name}`}
                    target="_blank"
                    rel="noopener noreferrer"
                />
            );
        }
        
        // Если есть внешняя ссылка
        if (document?.ExternalLink) {
            return (
                <Link
                    href={document.ExternalLink}
                    children={document.Name}
                    color={linkColor}
                    fontFamily='Roboto, sans-serif'
                    fontSize='1rem'
                    fontWeight='500'
                    aria-label={`Перейти к документу: ${document.Name} (откроется в новой вкладке)`}
                    target="_blank"
                    rel="noopener noreferrer"
                />
            );
        }
        
        // Fallback
        if (fallbackUrl) {
            return (
                <Link
                    href={fallbackUrl}
                    children={fallbackText}
                    color={linkColor}
                    fontFamily='Roboto, sans-serif'
                    fontSize='1rem'
                    fontWeight='500'
                    aria-label={fallbackAriaLabel}
                    target="_blank"
                    rel="noopener noreferrer"
                />
            );
        }
        
        return <span style={{ color: textColor }}>{document?.Name || fallbackText}</span>;
    };

    const renderSection = (title, id, documents, fallbackItems = []) => {
        const hasDocuments = documents && documents.length > 0;
        const itemsToRender = hasDocuments ? documents : fallbackItems;

        return (
            <section aria-labelledby={id}>
                <h4 id={id} className={style.header} style={{ color: textColor }}>
                    {title}
                </h4>
                <div className={style.text} style={{ color: textColor }}>
                    <ul className={style.list}>
                        {itemsToRender.map((item, index) => (
                            <li key={item.id || index}>
                                {hasDocuments ? (
                                    <>
                                        <DocumentLink document={item} />
                                        {item.File?.data?.attributes?.size && (
                                            <span style={{ color: textColor, fontSize: '0.8rem', marginLeft: '8px' }}>
                                                ({(item.File.data.attributes.size / 1024).toFixed(1)} KB)
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    <DocumentLink 
                                        document={null}
                                        fallbackUrl={item.href}
                                        fallbackText={item.text}
                                        fallbackAriaLabel={item.ariaLabel}
                                    />
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            </section>
        );
    };

    // Fallback items for sections not in API
    const federalStandardsFallback = [
        {
            href: "https://fgos.ru/",
            text: "ФГОС среднего профессионального образования по направлению 'Физическая культура и спорт'",
            ariaLabel: "ФГОС среднего профессионального образования (откроется в новой вкладке)"
        },
        {
            href: "https://fgos.ru/",
            text: "ФГОС дополнительного образования детей",
            ariaLabel: "ФГОС дополнительного образования детей (откроется в новой вкладке)"
        }
    ];

    const federalRequirementsFallback = [
        {
            href: "https://www.minsport.gov.ru/",
            text: "ФГТ к минимуму содержания, структуре, условиям реализации программ спортивной подготовки",
            ariaLabel: "ФГТ к программам спортивной подготовки (откроется в новой вкладке)"
        },
        {
            href: "https://www.minsport.gov.ru/",
            text: "ФГТ к дополнительным предпрофессиональным программам",
            ariaLabel: "ФГТ к дополнительным предпрофессиональным программам (откроется в новой вкладке)"
        }
    ];

    const localStandardsFallback = [
        {
            href: "#",
            text: "Нет доступных документов",
            ariaLabel: "Документы отсутствуют"
        }
    ];

    const internalRequirementsFallback = [
        {
            href: "#", 
            text: "Нет доступных документов",
            ariaLabel: "Документы отсутствуют"
        }
    ];

    if (loading) {
        return (
            <Block
                background={backgroundColor}
                width="75%"
                height="200px"
                justifyContent="center"
                alignItems="center"
            >
                <p style={{ color: textColor }}>Загрузка данных...</p>
            </Block>
        );
    }

    if (error) {
        return (
            <Block
                background={backgroundColor}
                width="75%"
                height="200px"
                justifyContent="center"
                alignItems="center"
            >
                <p style={{ color: textColor }}>{error}</p>
            </Block>
        );
    }

    return (
        <Block
            flexDirection='column'
            justifyContent='start'
            alignItems='start'
            width="75%"
            height='fit-content'
            background={backgroundColor}
            header='ОБРАЗОВАТЕЛЬНЫЕ СТАНДАРТЫ И ТРЕБОВАНИЯ'
            headerColor={textColor}
            id='requirements'
            aria-label="Образовательные стандарты и требования"
            role="region"
        >
            <FadeIn>
                <div className={style.content}>
                    <div className={style.wrapperData}>
                        {renderSection(
                            "Федеральные государственные образовательные стандарты (ФГОС)",
                            "federal-standards",
                            data?.FederalStandards,
                            federalStandardsFallback
                        )}

                        {renderSection(
                            "Федеральные государственные требования (ФГТ)",
                            "federal-requirements", 
                            data?.FederalRequirements,
                            federalRequirementsFallback
                        )}

                        {renderSection(
                            "Образовательные стандарты организации",
                            "local-standards",
                            data?.LocalStandards,
                            localStandardsFallback
                        )}

                        {renderSection(
                            "Самостоятельно устанавливаемые требования",
                            "internal-requirements",
                            data?.InternalRequirements, 
                            internalRequirementsFallback
                        )}
                    </div>
                </div>
            </FadeIn>
        </Block>
    );
};

export default RequirementsBlock;